/* Goodwood: form-local personalization for the approved family sign and cribbage board. */
if (!customElements.get('gdc-personalization')) {
  customElements.define('gdc-personalization', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }

    initialize() {
      const form = this.closest('form');
      const field = this.querySelector('[data-gdc-detail]');
      if (!form || !field) return false;
      if (this.form === form && this.field === field) return true;
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.form = form;
      this.field = field;
      this.group = this.querySelector('[data-gdc-detail-group]');
      this.error = this.querySelector('[data-gdc-error]');
      this.monogramIds = new Set((this.dataset.monogramVariants || '').split(',').map(id => id.trim()).filter(Boolean));
      const options = { signal: this.listeners.signal };
      form.addEventListener('change', event => {
        if (event.target.name === 'id') this.sync();
      }, options);
      field.addEventListener('input', () => this.clearError(), options);
      field.addEventListener('invalid', () => this.showError(), options);
      form.addEventListener('submit', event => {
        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      form.addEventListener('formdata', event => {
        this.sync();
        // Never attach a stale last name to a No Engraving variant.
        event.formData.delete(field.name);
        if (!field.disabled) event.formData.set(field.name, field.value.trim());
        this.querySelectorAll('[data-gdc-artwork], [data-gdc-extra]').forEach(input => {
          if (input.disabled) event.formData.delete(input.name);
        });
      }, options);
      this.sync();
      return true;
    }

    sync() {
      if (!this.form || !this.field) return;
      const variantId = this.form.querySelector('[name="id"]')?.value || '';
      const needed = this.dataset.kind === 'family' || this.monogramIds.has(String(variantId));
      this.group.hidden = !needed;
      this.field.disabled = !needed;
      this.field.required = needed;
      this.querySelectorAll('[data-gdc-artwork], [data-gdc-extra]').forEach(input => {
        input.disabled = !needed;
        if (!needed) input.value = '';
      });
      if (this.lastNeeded !== needed) this.clearError();
      this.lastNeeded = needed;
    }

    clearError() {
      this.field?.setCustomValidity('');
      this.field?.removeAttribute('aria-invalid');
      if (this.error) { this.error.hidden = true; this.error.textContent = ''; }
    }

    showError() {
      const message = this.dataset.kind === 'family'
        ? 'Please enter your family name and established date, or the exact wording for your sign.'
        : 'Please enter the last name for your monogram, or choose No Engraving.';
      this.field.setCustomValidity(message);
      this.field.setAttribute('aria-invalid', 'true');
      this.error.textContent = message;
      this.error.hidden = false;
    }

    validate() {
      if (!this.initialize()) return false;
      this.sync();
      if (this.field.disabled) return true;
      if (!this.field.value.trim()) {
        this.showError();
        this.field.focus();
        this.field.reportValidity();
        return false;
      }
      this.clearError();
      this.field.value = this.field.value.trim();
      return this.field.reportValidity();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.form = null;
      this.field = null;
    }
  });
}

// Resolve a safe same-store product-page destination for direct quick-add fallbacks.
function gdcRequiredDetailsUrl(variantId, origin, root, search) {
  const pages = {
    '40185696452663': 'bourbon-barrel-stave-personalized-family-01255',
    '42440701214775': 'bourbon-stave-cribbage-board-man-cave-24278',
    '42440701280311': 'bourbon-stave-cribbage-board-man-cave-24278',
    '42440701345847': 'bourbon-stave-cribbage-board-man-cave-24278'
  };
  if (!Object.prototype.hasOwnProperty.call(pages, variantId)) return null;
  const target = new URL(`${root}products/${pages[variantId]}`, origin);
  target.searchParams.set('variant', variantId);
  const preview = new URLSearchParams(search).get('preview_theme_id');
  if (preview) target.searchParams.set('preview_theme_id', preview);
  return target.href;
}

if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const personalization = this.form.querySelector('gdc-personalization');
        if (personalization) {
          if (typeof personalization.validate !== 'function' || !personalization.validate()) return;
        } else {
          // Guard direct quick-add forms that do not render the main product fields.
          // These are the current approved family-sign and monogram variant IDs.
          const target = gdcRequiredDetailsUrl(
            this.variantIdInput?.value,
            window.location.origin,
            window.Shopify?.routes?.root || '/',
            window.location.search
          );
          if (target) {
            window.location.assign(target);
            return;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        // Also reflect loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
          : undefined;
        const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
        if (stickyAddButton && stickyLoadingSpinner) {
          stickyAddButton.classList.add('loading');
          stickyLoadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Native FormData already contains fields belonging to this form.
        // Only collect legacy unassociated fields in this same product instance.
        const propertyScope = productInfo || this;
        const properties = propertyScope.querySelectorAll('.product-form__input [name^="properties["]');
        properties.forEach(property => {
          if (property.matches(':disabled') || property.form) return;
          if (productInfo && property.closest('product-info') !== productInfo) return;
          const owner = property.closest('product-form-component');
          if (owner && owner !== this) return;
          if ((property.type === 'checkbox' || property.type === 'radio') && !property.checked) return;
          if (formData.has(property.name)) return;
          if (property.type === 'file') {
            if (property.files?.length) formData.append(property.name, property.files[0]);
            return;
          }
          if (property.value != null && property.value !== '') formData.append(property.name, property.value);
        });

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = `${window.routes?.cart_url}`;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => {
                  if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(response);
                  }
                });
              },{ once: true });
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(personalization?.dataset.kind === 'family'
              ? 'We could not confirm that this item was added. Please check your cart before trying again. Your personalization and instructions are still on this page.'
              : 'We could not confirm that this item was added. Please check your cart before trying again. Your artwork and instructions are still on this page.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

            // Clear loading state on the sticky add-to-cart button (if present)
            const productInfo = this.closest('product-info');
            const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
            const stickyAddButton = sectionId
              ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
              : undefined;
            const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
            if (stickyAddButton && stickyLoadingSpinner) {
              stickyAddButton.classList.remove('loading');
              stickyLoadingSpinner.classList.add('hidden');
            }
          });
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
